import { useCallback, useEffect, useState } from "react";

/**
 * 横スクロールする要素の状態を追い、左右へスクロールさせる。
 *
 * 一覧テーブルの左右端に出すスクロールボタン（ngtg_msc の `_ScrollEdgeButton`）
 * のためのもの。要素はあとから差し替わる（読み込み中は描画されない）ので、
 * ref オブジェクトではなくコールバック ref + state で受ける。
 */
export function useHorizontalScroll(step = 400) {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!element) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const sync = () => {
      const max = element.scrollWidth - element.clientWidth;
      // 端の判定は 1px の余裕を持たせる（小数のズレで押せない状態を避ける）。
      setCanScrollLeft(element.scrollLeft > 1);
      setCanScrollRight(element.scrollLeft < max - 1);
    };

    sync();
    element.addEventListener("scroll", sync, { passive: true });
    // 表示列や列幅を変えると溢れ方が変わるので、中身の幅も監視する。
    const observer = new ResizeObserver(sync);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);

    return () => {
      element.removeEventListener("scroll", sync);
      observer.disconnect();
    };
  }, [element]);

  const scrollBy = useCallback(
    (direction: -1 | 1) => {
      element?.scrollBy({ left: direction * step, behavior: "smooth" });
    },
    [element, step],
  );

  return {
    /** スクロールさせたい要素に渡す ref。 */
    scrollRef: setElement,
    scrollBy,
    /** 左へスクロールできるか（ボタンの活性判定に使う）。 */
    canScrollLeft,
    /** 右へスクロールできるか。 */
    canScrollRight,
  };
}
